import { assets } from '@cmg/common';
import { Box, Divider, Grid, InfoOutlinedIcon, Stack, Typography } from '@cmg/design-system';
import { FormattedNumber } from '@cmg/i18n';
import React, { useMemo } from 'react';

import { useMatchingCmgEntityKeys } from '../../../../../../common/hooks/useMatchingCmgEntityKeys';
import { getOfferingTypeLabel } from '../../../../../../common/util/offering/offeringTypeHelpers';
import { OfferingStatusChip } from '../../../../../../design-system/components/data-display/offering-status-chip/OfferingStatusChip';
import { sectorLabels } from '../../../../../constants';
import { SideBar_OfferingDetails_OfferingPartsFragment } from '../../graphql';
import { SidePanelNavBarSection } from '../design-system';
import { CurrencyRangeValue } from './CurrencyRangeValue';
import { CountryCodeLayout, Flag, OnPlatformLogo } from './design-system';
import { getSidePanelPriceRangeValue } from './getSidePanelPriceRangeValue';

export type Props = {
  readonly isBusy?: boolean;
  publishedOffering: SideBar_OfferingDetails_OfferingPartsFragment;
};

const OfferingStatusIndicator: React.FC<Props> = props => {
  const { publishedOffering, isBusy } = props;
  const matchingCmgEntityKeys = useMatchingCmgEntityKeys();
  const isCurrentUsersFirmUnderwriting = useMemo(
    () =>
      publishedOffering?.syndicate?.managers.some(manager =>
        matchingCmgEntityKeys.includes(manager.cmgEntityKey)
      ),
    [publishedOffering, matchingCmgEntityKeys]
  );

  const pricingInstrument = useMemo(
    () =>
      publishedOffering?.instruments.find(
        instrument => instrument.id === publishedOffering.pricingInstrumentId
      ),
    [publishedOffering]
  );

  const priceRangeValue = useMemo(
    () => getSidePanelPriceRangeValue(publishedOffering),
    [publishedOffering]
  );

  return (
    <React.Fragment>
      <SidePanelNavBarSection isBusy={isBusy}>
        <Stack spacing={1}>
          <Typography variant="h3">{publishedOffering?.issuer.name}</Typography>
          <Typography variant="body2" color="text.secondary">
            {publishedOffering?.pricingInstrumentStockSymbol}
          </Typography>
        </Stack>
        <Stack spacing={1.5}>
          {publishedOffering?.isExecutingOnPlatform && (
            <Stack direction="row" spacing={2.5} alignItems="center">
              <Box minWidth={20}>
                <OnPlatformLogo
                  src={assets.logoNoText}
                  alt="This offering is being managed on the CMG Platform."
                />
              </Box>
              <Typography variant="body2" color="text.secondary">
                This offering is being managed on the CMG Platform.
              </Typography>
            </Stack>
          )}
          {isCurrentUsersFirmUnderwriting && (
            <Stack direction="row" spacing={2.5} alignItems="center">
              <Typography variant="body2" color="info.main">
                <InfoOutlinedIcon fontSize="small" />
              </Typography>
              <Typography variant="body2" color="text.secondary">
                My firm is participating in this deal
              </Typography>
            </Stack>
          )}
        </Stack>
      </SidePanelNavBarSection>
      <Divider />
      <SidePanelNavBarSection isBusy={isBusy}>
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Typography variant="specialLabel" color="text.secondary">
              Type
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="highlight2" color="text.primary">
              {getOfferingTypeLabel(publishedOffering.type)}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="specialLabel" color="text.secondary">
              Status
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <OfferingStatusChip status={publishedOffering.status} />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="specialLabel" color="text.secondary">
              Sector
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="highlight2" color="text.primary">
              {publishedOffering?.issuer.sector
                ? sectorLabels[publishedOffering.issuer.sector]
                : null}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="specialLabel" color="text.secondary">
              Region
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="highlight2" color="text.primary">
              {pricingInstrument?.region ? pricingInstrument?.region : '-'}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="specialLabel" color="text.secondary">
              Country
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <CountryCodeLayout>
              <Flag countryCode={pricingInstrument?.countryCode} />
              <Typography variant="highlight2" color="text.primary">
                {pricingInstrument?.countryCode}
              </Typography>
            </CountryCodeLayout>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="specialLabel" color="text.secondary">
              Offering Size
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="highlight2" color="text.primary">
              <FormattedNumber
                value={publishedOffering.sizeInCurrency}
                format="currencyCompact"
                currency={publishedOffering.pricingCurrencyCode}
              />
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="specialLabel" color="text.secondary">
              {priceRangeValue.label}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            {priceRangeValue.kind === 'range' ? (
              <CurrencyRangeValue
                currencyCode={publishedOffering.pricingCurrencyCode}
                rangeLow={priceRangeValue.low}
                rangeHigh={priceRangeValue.high}
              />
            ) : (
              <Typography variant="highlight2" color="text.primary" role="cell">
                {priceRangeValue.value !== null ? (
                  <FormattedNumber
                    value={priceRangeValue.value}
                    format="currency"
                    currency={publishedOffering.pricingCurrencyCode}
                  />
                ) : (
                  '-'
                )}
              </Typography>
            )}
          </Grid>
        </Grid>
      </SidePanelNavBarSection>
    </React.Fragment>
  );
};

export default OfferingStatusIndicator;
